import * as GeoJSON from 'geojson'
import { debounce } from 'lodash-es'
import { DateTime, Duration } from 'luxon'
import dynamic from 'next/dynamic'
import { useContext, useEffect, useMemo, useState } from 'react'
import { ClusterFeature, PointFeature } from 'supercluster'

import { useSettings } from '@/feature/organizations/api/useSettings'
import { MapContext } from '@/feature/map'
import {
  ClusterProperties,
  PointProperties,
  SuperClustererExternalClusterer,
} from '@/feature/map/utils/SuperClustererExternalClusterer'
import {
  AssetMarker,
  ClusterMarker,
  OBSERVATORY_MAP_VIEWPORT_PADDING_RATIO,
  useMapClusteringOptions,
  usePersistentObservatoryPageStore,
} from '@/feature/observatory'
import { useObservatoryAssets } from '@/feature/observatory/hooks/useObservatoryAssets'
import { useObservatoryGeoFencesAside } from '@/feature/observatory/hooks/useObservatoryGeoFences'
import { AssetTrackingData } from '@/feature/telemetry'
import { useTelemetryStore } from '@/feature/telemetry/store/telemetry'

const getPaddedViewport = (
  bounds: google.maps.LatLngBounds,
  projection: google.maps.MapCanvasProjection,
  paddingX: number,
  paddingY: number,
): GeoJSON.BBox => {
  const northEast = bounds.getNorthEast()
  const nePx = projection.fromLatLngToDivPixel(northEast)!
  nePx.x += paddingX
  nePx.y -= paddingY
  const paddedNorthEast = projection.fromDivPixelToLatLng(nePx)!

  const southWest = bounds.getSouthWest()
  const swPx = projection.fromLatLngToDivPixel(southWest)!
  swPx.x -= paddingX
  swPx.y += paddingY
  const paddedSouthWest = projection.fromDivPixelToLatLng(swPx)!

  // Compute longitudes with simple guardrails for world-wrap.
  // When padding pushes the bbox across the antimeridian, paddedSouthWest.lng() can
  // end up numerically greater than paddedNorthEast.lng() due to wrap-around.
  // In that case, treat the horizontal extent as the full world to avoid dropping all points.
  let west = southWest.lng() == -180 ? southWest.lng() : paddedSouthWest.lng()
  let east = northEast.lng() == 180 ? northEast.lng() : paddedNorthEast.lng()

  if (west > east) {
    // Crosses the antimeridian after padding – include the whole world horizontally
    west = -180
    east = 180
  }

  return [west, paddedSouthWest.lat(), east, paddedNorthEast.lat()]
}

export const CollectiveAssetMarkersRender = () => {
  const setSelectedItem = usePersistentObservatoryPageStore((state) => state.setSelectedItem)
  // Read telemetry on-demand to avoid re-renders on every telemetry update
  // Individual AssetMarker components will subscribe to their own telemetry
  const getTelemetryData = () => useTelemetryStore.getState().tracking
  const assets = useObservatoryAssets()
  const geoFences = useObservatoryGeoFencesAside()

  // Ensure telemetry websocket stays active
  const incrementTelemetryListeners = useTelemetryStore((s) => s.incrementActiveListeners)
  const decrementTelemetryListeners = useTelemetryStore((s) => s.decrementActiveListeners)
  useEffect(() => {
    incrementTelemetryListeners()
    return () => decrementTelemetryListeners()
  }, [incrementTelemetryListeners, decrementTelemetryListeners])

  const map = useContext(MapContext)
  const [mapZoom, setMapZoom] = useState(0)
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | undefined>()

  useEffect(() => {
    if (map?.map) {
      const zoom = map.map.getZoom() ?? 0
      setMapZoom(zoom)
      const update = () => {
        const newZoom = map.map.getZoom() ?? 0
        setMapZoom(newZoom)
        const newBounds = map.map.getBounds()
        if (newBounds) {
          setMapBounds(newBounds)
        }
      }
      update()

      // 350ms is a good unit
      // It catches and debounces slower user mouse scroll events
      const handler = debounce(update, 350, { leading: false, trailing: true })
      const idleListener = map.map.addListener('idle', handler)

      return () => {
        handler.cancel()
        idleListener.remove()
      }
    }
  }, [map?.map])
  const clusteringOptions = useMapClusteringOptions()

  const clusterer = useMemo(() => {
    return new SuperClustererExternalClusterer<AssetTrackingData>({
      map: map?.map,
      ...clusteringOptions.options,
    })
  }, [map?.map, clusteringOptions])

  const { data: settings } = useSettings()
  const assetsFilters = usePersistentObservatoryPageStore((state) => state.assetsFilters)
  const selectedItem = usePersistentObservatoryPageStore((state) => state.selectedItem)
  const offlineOnlyToggle = assetsFilters?.toggles?.['Offline Only'] ?? false

  const gatherClusterPoints = useMemo(() => {
    const bounds = map?.map?.getBounds()
    const projection = clusterer.getProjection()
    const zoom = map?.map?.getZoom()

    if (!bounds || !projection || !zoom) {
      return []
    }

    const allowedIds = new Set(assets.data?.results?.map((asset) => asset.vehicleBridgeId))

    const mapDiv = map?.map?.getDiv()
    const padX = Math.round((mapDiv?.clientWidth ?? 0) * OBSERVATORY_MAP_VIEWPORT_PADDING_RATIO)
    const padY = Math.round((mapDiv?.clientHeight ?? 0) * OBSERVATORY_MAP_VIEWPORT_PADDING_RATIO)
    const bbox = getPaddedViewport(bounds, projection, padX, padY)

    // Read telemetry on-demand (doesn't cause re-renders)
    const telemetryData = getTelemetryData()

    // Telemetry limited to assets that belong to the current organization
    const organizationTelemetry = Array.from(telemetryData.values()).filter((d) =>
      allowedIds.has(d.sourceId),
    )

    const offlineAfterMins = settings?.vehicleOfflineAfter ?? 24 * 60
    // Telemetry that passes the offline toggle and is therefore eligible for rendering
    const renderableTelemetry = offlineOnlyToggle
      ? organizationTelemetry.filter(
          (d) =>
            DateTime.fromJSDate(d.sensorDatetime).diffNow() <
            Duration.fromObject({ minutes: -offlineAfterMins }),
        )
      : organizationTelemetry

    // Keep only telemetry that already falls inside the padded viewport
    const telemetryInViewport = renderableTelemetry.filter(
      (d) =>
        d.longitude >= bbox[0] &&
        d.longitude <= bbox[2] &&
        d.latitude >= bbox[1] &&
        d.latitude <= bbox[3],
    )

    const selectedAsset =
      selectedItem?.type === 'asset'
        ? assets.data?.results?.find((asset) => asset.id === selectedItem.id)
        : undefined

    const selectedAssetTelemetry = selectedAsset
      ? renderableTelemetry.find((d) => d.sourceId === selectedAsset.vehicleBridgeId)
      : undefined

    const telemetryForClusterer =
      selectedAssetTelemetry &&
      !telemetryInViewport.some((d) => d.sourceId === selectedAssetTelemetry.sourceId)
        ? telemetryInViewport.concat(selectedAssetTelemetry)
        : telemetryInViewport

    clusterer.load(telemetryForClusterer)

    const allClusters = clusterer.getClusters(bbox, Math.round(zoom))

    // Track telemetry that has not yet been represented by the clusterer; Map keeps deletes O(1) and values are already viewport constrained
    const unclusteredTelemetryMap = new Map(
      telemetryForClusterer.map((d) => [d.sourceId, d] as const),
    )
    // Remove any telemetry already emitted by the cluster results above
    const unclusteredTelemetry = allClusters.reduce((missing, cluster) => {
      if ('items' in cluster.properties) {
        for (const item of cluster.properties.items) {
          missing.delete(item.sourceId)
        }
      } else {
        const trackingData = cluster.properties.item as AssetTrackingData
        missing.delete(trackingData.sourceId)
      }
      return missing
    }, unclusteredTelemetryMap)

    return allClusters.concat(
      Array.from(
        unclusteredTelemetry.values(),
        (data) =>
          ({
            properties: {
              item: data,
            },
          }) as PointFeature<PointProperties<AssetTrackingData>>,
      ),
    )
  }, [
    clusterer,
    // Note: telemetryData is read on-demand via getTelemetryData() to avoid re-renders
    // The viewport filtering uses current telemetry positions when bounds/zoom change
    map,
    assets.data?.results,
    mapZoom,
    mapBounds,
    settings?.vehicleOfflineAfter,
    offlineOnlyToggle,
    selectedItem,
  ])

  const markers = useMemo(() => {
    return gatherClusterPoints.map((cluster) => {
      if ('cluster' in cluster.properties) {
        const clusterInfo = cluster as ClusterFeature<ClusterProperties<AssetTrackingData>>
        return (
          <ClusterMarker
            key={`cluster-${cluster.id}`}
            position={{
              lat: cluster.geometry.coordinates[1],
              lng: cluster.geometry.coordinates[0],
            }}
            items={clusterInfo.properties.items}
          />
        )
      } else {
        const trackingData = cluster.properties.item as AssetTrackingData
        const asset = assets.data?.results?.find(
          (asset) => asset.vehicleBridgeId === trackingData.sourceId,
        )

        if (!asset) {
          return null
        }

        return (
          <AssetMarker
            key={`asset-${asset.id}`}
            asset={asset}
            onClick={() => {
              setSelectedItem({ id: asset.id, type: 'asset' })
            }}
          />
        )
      }
    })
  }, [gatherClusterPoints, setSelectedItem, geoFences.data?.results, assets.data?.results])

  return markers
}

export const CollectiveAssetMarkers = dynamic(() => Promise.resolve(CollectiveAssetMarkersRender), {
  ssr: false,
})
